import { alpha, createTheme, darken, Theme } from '@mui/material/styles';

interface ClientPaletteTest {
  primary: {
    main: string;
  };
  secondary: {
    main: string;
  };
}
declare module '@mui/material/styles/createPalette' {
  interface CommonColors {
    grey: string;
    danger: string;
  }
  interface Palette {
    select: {
      hover: string;
    };
  }
  interface PaletteOptions {
    select?: {
      hover?: string;
    };
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xxl: true;
    xxxl: true;
  }
  interface Components {
    MuiPickersPopper: {
      styleOverrides: {
        root: {
          '& .MuiDialogActions-root': {
            '& .MuiButtonBase-root': {
              width: string;
              height: string;
            };
          };
        };
      };
    };
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          '&.Mui-selected': {
            backgroundColor: string;
            '&:focus': {
              backgroundColor: string;
            };
          };
        };
      };
    };
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          '& .MuiMenuItem-root': {
            '&.Mui-selected': {
              backgroundColor: string;
              '&:hover': {
                backgroundColor: string;
              };
            };
            '&:hover': {
              backgroundColor: string;
            };
          };
          '& .MuiList-root': {
            marginRight: string;
            ':after': {
              height: string;
            };
            '&::-webkit-scrollbar': {
              width: string;
            };
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: string;
              borderRadius: string;
              '&:hover': {
                backgroundColor: string;
              };
            };
          };
        };
      };
    };
  }
}

export const getTheme = (clientPalette: ClientPaletteTest): Theme => {
  return createTheme({
    palette: {
      primary: {
        main: clientPalette.primary.main,
      },
      secondary: {
        main: clientPalette.secondary.main,
      },
      common: {
        white: '#FAFCFF',
        grey: '#595959',
        danger: '#FF334B',
      },
      select: {
        hover: '#EDEDED',
      },
    },
    typography: {
      fontFamily: `"Montserrat", "Roboto", "sans-serif"`,
      allVariants: {
        color: '#595959',
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1600,
        xxl: 1920,
        xxxl: 3840,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '30px',
            width: '280px',
            height: '48px',
            textTransform: 'none',
            backgroundColor: clientPalette.secondary.main,
            color: '#FAFCFF',
            fontFamily: 'Montserrat, sans-serif',
            boxShadow: 'none',
            fontSize: '16px',
            fontWeight: '400',
            '&:hover': {
              backgroundColor: darken(clientPalette.secondary.main, 0.1),
              color: '#FAFCFF',
            },
            '&:active': {
              backgroundColor: clientPalette.secondary.main,
              color: '#FAFCFF',
            },
            '&:disabled': {
              opacity: '40%',
              color: '#FAFCFF',
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: '#595959',
              '& + .MuiSwitch-track': {
                backgroundColor: '#707070',
              },
              '&.Mui-disabled': {
                color: '#595959',
                '& + .MuiSwitch-track': {
                  backgroundColor: '#707070',
                  opacity: 0.5,
                },
              },
            },
            '&.Mui-disabled': {
              '& + .MuiSwitch-track': {
                opacity: 0.3,
              },
            },
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            fontFamily: 'Montserrat, sans-serif',
            '&.MuiPickersDay-root': {
              '&.MuiPickersDay-today': {
                backgroundColor: 'transparent',
              },
              '&:active': {
                backgroundColor: alpha('#595959', 0.3),
              },
              '&.Mui-selected': {
                backgroundColor: alpha('#595959', 0.3),
                ':focus': {
                  backgroundColor: alpha('#595959', 0.3),
                },
              },
            },
          },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            '&.Mui-selected': {
              backgroundColor: alpha('#595959', 0.3),
              '&:focus': {
                backgroundColor: alpha('#595959', 0.3),
              },
            },
          },
        },
      },
      MuiPickersPopper: {
        styleOverrides: {
          root: {
            '& .MuiDialogActions-root': {
              '& .MuiButtonBase-root': {
                width: '140px',
                height: '42px',
              },
            },
          },
        },
      },
      MuiMultiSectionDigitalClock: {
        styleOverrides: {
          root: {
            '& .MuiList-root': {
              marginRight: '10px',
              ':after': {
                height: '20px',
              },
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: clientPalette.secondary.main,
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: clientPalette.secondary.main,
                },
              },
            },
            '& .MuiMenuItem-root': {
              '&.Mui-selected': {
                backgroundColor: alpha('#595959', 0.3),
                '&:hover': {
                  backgroundColor: alpha('#595959', 0.3),
                },
              },
              '&:hover': {
                backgroundColor: '#EDEDED',
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: 'inherit',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            minWidth: '1280px',
            minHeight: '720px',
            height: '100%',
            margin: '0 auto',
          },
          body: {
            backgroundColor: '#FAFCFF',
          },
        },
      },
    },
  });
};
